/**
 * React Query hooks da Tela A2 — Config Tenant.
 *
 * GET/PATCH `/v1/tenant-config` (gate `tenant-config-read` / `tenant-config-manage`)
 * + GET/POST `/v1/tenant-operation-profile(/apply)` (#313). Após cada mutation,
 * invalida o cache local da query correspondente.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../lib/api-client';

import type {
  OperationProfileId,
  OperationProfileStateWire,
  SerializedTenantConfig,
  UpdateTenantConfigRequest,
} from './types';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

export const TENANT_CONFIG_QUERY_KEY = ['tenant-config'] as const;
export const OPERATION_PROFILE_QUERY_KEY = ['tenant-operation-profile'] as const;

export function useTenantConfig(enabled = true): UseQueryResult<SerializedTenantConfig> {
  return useQuery({
    queryKey: TENANT_CONFIG_QUERY_KEY,
    queryFn: async () => (await api.getTenantConfig()).data,
    enabled,
  });
}

export function useUpdateTenantConfig(): UseMutationResult<
  SerializedTenantConfig,
  Error,
  UpdateTenantConfigRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.updateTenantConfig(body)).data,
    onSuccess: (data) => {
      qc.setQueryData(TENANT_CONFIG_QUERY_KEY, data);
    },
  });
}

export function useOperationProfile(enabled = true): UseQueryResult<OperationProfileStateWire> {
  return useQuery({
    queryKey: OPERATION_PROFILE_QUERY_KEY,
    queryFn: async () => (await api.getOperationProfile()).data,
    enabled,
  });
}

export function useApplyOperationProfile(): UseMutationResult<
  OperationProfileStateWire,
  Error,
  OperationProfileId
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (profile) => (await api.applyOperationProfile(profile)).data,
    onSuccess: (data) => {
      qc.setQueryData(OPERATION_PROFILE_QUERY_KEY, data);
    },
  });
}
